import {  t  } from "@/client/libs/dummy-i18n";
import { Button } from "@stackmorph/ui";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import Tilt from "react-parallax-tilt";
import { useNavigate } from "react-router";

export const HeroSection = () => {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => { window.removeEventListener("mousemove", handleMouseMove); };
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-background pt-20">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      
      <motion.div
        className="pointer-events-none absolute h-96 w-96 rounded-full bg-gradient-to-r from-indigo-500/20 via-cyan-500/20 to-emerald-500/20 blur-3xl"
        animate={{
          x: mousePosition.x - 192,
          y: mousePosition.y - 192,
        }}
        transition={{ type: "spring", damping: 30, stiffness: 200 }}
      />

      <div className="container relative z-10 mx-auto px-6 py-12">
        <div className="grid gap-4 lg:grid-cols-12 lg:grid-rows-12 lg:h-[800px]">
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="lg:col-span-7 lg:row-span-8 rounded-3xl border border-border/50 bg-gradient-to-br from-background/80 to-background/40 p-8 backdrop-blur-xl lg:p-12"
          >
            <div className="flex h-full flex-col justify-between">
              <div className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                  </span>
                  {t`Live Preview`}
                </motion.div>

                <h1 className="font-bold leading-none tracking-tighter">
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="block text-5xl lg:text-7xl"
                  >
                    {t`Your career`}
                  </motion.span>
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-2 block bg-gradient-to-r from-indigo-600 via-cyan-600 to-emerald-600 bg-clip-text text-6xl text-transparent lg:text-8xl"
                  >
                    {t`decoded`}
                  </motion.span>
                </h1>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="max-w-lg text-lg text-muted-foreground lg:text-xl"
                >
                  {t`Build resumes that get you hired`}
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-wrap gap-3"
              >
                <Button
                  size="lg"
                  className="rounded-full bg-gradient-to-r from-indigo-600 to-cyan-600 px-8"
                  onClick={() => navigate("/auth/register")}
                >
                  {t`Start Building`}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => navigate("/auth/login")}
                >
                  {t`Examples`}
                </Button>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-5 lg:row-span-4 rounded-3xl border border-border/50 bg-gradient-to-br from-indigo-500/10 to-transparent p-6 backdrop-blur-xl"
          >
            <div className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t`Impact`}
              </h3>
              <div>
                <div className="text-4xl font-bold">2.3M+</div>
                <div className="text-sm text-muted-foreground">{t`Resumes Created`}</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="group lg:col-span-5 lg:row-span-4 rounded-3xl border border-border/50 bg-gradient-to-br from-cyan-500/10 to-transparent p-6 backdrop-blur-xl hover:border-cyan-500/50"
          >
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-500/10">
              <svg className="h-6 w-6 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-bold">{t`AI-Powered`}</h3>
            <p className="text-sm text-muted-foreground">{t`Smart suggestions`}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="lg:col-span-7 lg:row-span-4 rounded-3xl border border-border/50 bg-gradient-to-br from-background to-background/40 p-4 backdrop-blur-xl"
          >
            <Tilt tiltMaxAngleX={2} tiltMaxAngleY={2}>
              <img
                src="/screenshots/builder.jpg"
                alt="Resume Preview"
                className="rounded-2xl"
              />
            </Tilt>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            className="lg:col-span-5 lg:row-span-4 rounded-3xl border border-border/50 bg-gradient-to-br from-emerald-500/10 to-transparent p-6 backdrop-blur-xl"
          >
            <div className="flex h-full flex-col justify-between">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10">
                <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="mb-2 text-xl font-bold">{t`Privacy First`}</h3>
                <p className="text-sm text-muted-foreground">{t`100% secure`}</p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};
